import { Button, Col, Form, Input, InputNumber, Modal, Row, Select, Space, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import CustomFormDatePicker from "src/components/AntdCustomPart/CustomFormDatePicker/CustomFormDatePicker";
import FormLabel from "src/components/AntdCustomPart/FormLabel/FormLabel";
import ModalTitle from "src/components/AntdCustomPart/ModalTitle/ModalTitle";
import { AreaProps } from "src/entity/company/company-response.entity";
import { EmissionControl, SettingChemicalProp, SettingLoadingMethod } from "src/entity/settings/terminal-market";
import { Dropdown, LoadingInformation } from "src/entity/terminal-marketing/terminal-marketing.entity";
import { generateErrorMessage } from "src/utils/error-message";

const { Text } = Typography;

const SetupLoadingInfoModal = (props: {
  open: boolean;
  id: string;
  mode: "insert" | "update";
  initialData: LoadingInformation;
  loading: boolean;
  companyId: string;
  chemicalPropertyMaster: SettingChemicalProp[];
  loadingMethodMaster: SettingLoadingMethod[];
  emissionControlMaster: EmissionControl[];
  areaMaster: AreaProps[];
  isDuplicate: boolean;
  onDuplicateChange: (isDup: boolean) => void;
  onCloseModal: () => void;
  onSave: (data: LoadingInformation, id: string) => void;
}) => {
  const { chemicalPropertyMaster, loadingMethodMaster, emissionControlMaster, areaMaster, id, isDuplicate, onSave, onDuplicateChange } =
    props;
  const [form] = Form.useForm();
  const { open, mode, initialData, loading, onCloseModal } = props;
  const [information, setInformation] = useState<LoadingInformation>(initialData);
  const [optionChemical, setOptionChemical] = useState<Dropdown[]>([]);
  const [optionLoadingBy, setOptionLoadingBy] = useState<Dropdown[]>([]);
  const [optionArea, setOptionArea] = useState<Dropdown[]>([]);
  const [optionEmission, setOptionEmission] = useState<Dropdown[]>([]);
  const [errorDuplicate, setErrorDuplicate] = useState<string | null>(null);

  const handleSave = async () => {
    try {
      await form.validateFields();
      const formValue = form.getFieldsValue();
      onSave(formValue, id);
    } catch (error) {
      console.error(error);
    }
  };

  const initMasterData = () => {
    const tmpOptionChemical: Dropdown[] = chemicalPropertyMaster.map((item, index) => {
      return {
        key: String(index),
        value: item._id,
        label: item.chemicalName,
      };
    });
    const tmpOptionLoading: Dropdown[] = loadingMethodMaster.map((item, index) => {
      return {
        key: String(index),
        value: item._id,
        label: item.loadingBy,
      };
    });
    const tmpOptionEmission: Dropdown[] = emissionControlMaster.map((item, index) => {
      return {
        key: String(index),
        value: item._id,
        label: item.emissionControl,
      };
    });
    const tmpArea: Dropdown[] = areaMaster.map((item, index) => {
      return {
        key: String(index),
        value: item._id,
        label: item.areaName,
      };
    });
    setOptionChemical(tmpOptionChemical);
    setOptionLoadingBy(tmpOptionLoading);
    setOptionArea(tmpArea);
    setOptionEmission(tmpOptionEmission);
  };

  const handleSelectChange = (key: keyof LoadingInformation, value: string | number) => {
    if (information) {
      setInformation({
        ...information,
        [key]: value ?? "",
      });
    }
  };

  const handleValuesChange = (changedValues: any) => {
    if (changedValues.emissionControlId) {
      const efficiency = emissionControlMaster.find((item) => item._id === changedValues.emissionControlId)?.efficiency;
      form.setFieldsValue({
        efficiency: efficiency,
      });
    }
    if (changedValues.arrControlExceptionDate) {
      const newExceptions: string[] = Array.from(new Set<string>(changedValues.arrControlExceptionDate));
      form.setFieldsValue({
        arrControlExceptionDate: newExceptions,
      });
    }
    if (["chemicalName", "area", "loadingBy"].some((key) => changedValues[key])) {
      onDuplicateChange(false);
    }
  };

  useEffect(() => {
    if (!information.emissionControlId) {
      information.efficiency = 0;
    }
    const initializeForm = async () => {
      form.setFieldsValue({ ...information });
    };
    initMasterData();
    initializeForm();
  }, []);

  useEffect(() => {
    const errorText = isDuplicate ? "Chemical Name, Area and Loading By already exist in the system." : "";
    setErrorDuplicate(errorText);
    form.setFields(
      ["chemicalId", "area", "loadingById"].map((field) => ({
        name: field,
        errors: isDuplicate ? [""] : [],
      })),
    );
  }, [isDuplicate]);

  return (
    <Modal
      centered
      maskClosable={false}
      title={<ModalTitle title="Loading Information" />}
      open={open}
      onCancel={onCloseModal}
      width={900}
      footer={
        <Row className="w-full border-t" justify={"end"}>
          <Button className="mt-4" key="submit" type="primary" onClick={handleSave} loading={loading}>
            Save
          </Button>
        </Row>
      }
    >
      {errorDuplicate && (
        <>
          <Row className="w-full" justify={"center"}>
            <Text type="danger">{errorDuplicate}</Text>
          </Row>
        </>
      )}
      <Form size="middle" layout="vertical" autoComplete="off" form={form} requiredMark={false} onValuesChange={handleValuesChange}>
        <Row gutter={[16, 0]}>
          <Col xs={24} sm={12} lg={12}>
            <Form.Item
              label={<FormLabel label={"Chemical Name"} required />}
              name="chemicalId"
              rules={[{ required: true, message: generateErrorMessage("Chemical Name") }]}
            >
              <Select options={optionChemical} onChange={(value) => handleSelectChange("chemicalId", value)} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={12}>
            <Form.Item
              label={<FormLabel label={"Area"} required />}
              name="area"
              rules={[{ required: true, message: generateErrorMessage("Area") }]}
            >
              <Select options={optionArea} onChange={(value) => handleSelectChange("area", value)} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={12}>
            <Form.Item
              label={<FormLabel label={"Loading By"} required />}
              name="loadingById"
              rules={[{ required: true, message: generateErrorMessage("Loading By") }]}
            >
              <Select options={optionLoadingBy} onChange={(value) => handleSelectChange("loadingById", value)} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={12}>
            <Form.Item label={<FormLabel label={"Emission Control"} />} name="emissionControlId">
              <Select
                allowClear
                options={optionEmission}
                onChange={(value) => {
                  handleSelectChange("emissionControlId", value);

                  // If the clear button is clicked (value is undefined), set efficiency is 0
                  if (value === undefined) {
                    form.setFieldsValue({
                      efficiency: 0,
                      emissionControlId: "",
                    });
                  }
                }}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={12}>
            <Form.Item label={<FormLabel label={"Efficiency"} />} name="efficiency">
              <InputNumber defaultValue={0} style={{ width: "100%" }} disabled={true} addonAfter={"%"} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} lg={12} className={"date-picker-group"}>
            <CustomFormDatePicker
              name={"arrControlExceptionDate"}
              label={"Control Exception Date"}
              customClassName={"exception-date-period"}
              mode={"date"}
            />
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default SetupLoadingInfoModal;
